// Validation utilities for Digital ID App
// Comprehensive input validation and sanitization

// Email validation with detailed error messages
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email.trim())) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }
  
  return { isValid: true, value: email.trim().toLowerCase() };
};

// Legacy email validation for backward compatibility
export const validateEmailSimple = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation (international format)
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Remove all non-digit characters except +
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  
  // Must start with + and have 8-15 digits
  const phoneRegex = /^\+\d{8,15}$/;
  if (!phoneRegex.test(cleanPhone)) {
    return { isValid: false, error: 'Please enter a valid phone number with country code (e.g., +62812345678)' };
  }
  
  return { isValid: true, value: cleanPhone };
};

// Legacy phone validation for backward compatibility
export const validatePhoneSimple = (phone) => {
  const phoneRegex = /^[+]?[0-9\s\-()]{8,15}$/;
  return phoneRegex.test(phone);
};

// WhatsApp validation
export const validateWhatsApp = (whatsapp) => {
  if (!whatsapp || typeof whatsapp !== 'string') {
    return { isValid: false, error: 'WhatsApp number is required' };
  }
  
  const cleanWA = whatsapp.replace(/[^\d+]/g, '');
  const waRegex = /^\+\d{8,15}$/;
  if (!waRegex.test(cleanWA)) {
    return { isValid: false, error: 'Please enter a valid WhatsApp number with country code (e.g., +62812345678)' };
  }
  
  return { isValid: true, value: cleanWA };
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters long` };
  }
  
  if (trimmedName.length > 100) {
    return { isValid: false, error: `${fieldName} is too long (max 100 characters)` };
  }
  
  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  if (!nameRegex.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, apostrophes, and dots` };
  }
  
  return { isValid: true, value: trimmedName };
};

export const validateRequired = (value) => {
  return value && value.trim() !== '';
};

export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password.length < 6) {
    return { isValid: false, error: 'Password must be at least 6 characters long' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long (max 128 characters)' };
  }
  
  // At least one letter and one number
  if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one letter and one number' };
  }
  
  return { isValid: true, value: password };
};

// Legacy password validation for backward compatibility
export const validatePasswordSimple = (password) => {
  return password && password.length >= 6;
};

export const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    return { isValid: false, error: 'Username is required' };
  }
  
  const trimmedUsername = username.trim();
  
  if (trimmedUsername.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters long' };
  }
  
  if (trimmedUsername.length > 20) {
    return { isValid: false, error: 'Username is too long (max 20 characters)' };
  }
  
  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  if (!usernameRegex.test(trimmedUsername)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, and underscores' };
  }
  
  return { isValid: true, value: trimmedUsername.toLowerCase() };
};

// Legacy username validation for backward compatibility
export const validateUsernameSimple = (username) => {
  const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
  return usernameRegex.test(username);
};

export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove onevent handlers
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''); // Remove script tags
};

// Check for XSS patterns
export const containsXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

export const validateUserForm = (userData) => {
  const errors = {};
  
  if (!validateRequired(userData.username)) {
    errors.username = 'Username is required';
  } else {
    const usernameValidation = validateUsername(userData.username);
    if (!usernameValidation.isValid) {
      errors.username = usernameValidation.error;
    }
  }
  
  if (!validateRequired(userData.email)) {
    errors.email = 'Email is required';
  } else {
    const emailValidation = validateEmail(userData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }
  }
  
  if (!validateRequired(userData.full_name)) {
    errors.full_name = 'Full name is required';
  } else {
    const nameValidation = validateName(userData.full_name);
    if (!nameValidation.isValid) {
      errors.full_name = nameValidation.error;
    }
  }
  
  if (!validateRequired(userData.password)) {
    errors.password = 'Password is required';
  } else {
    const passwordValidation = validatePassword(userData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error;
    }
  }
  
  if (!validateRequired(userData.role)) {
    errors.role = 'Role is required';
  }
  
  return errors;
};

export const validateDigitalCardForm = (cardData) => {
  const errors = {};
  
  if (!validateRequired(cardData.name)) {
    errors.name = 'Name is required';
  } else {
    const nameValidation = validateName(cardData.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.error;
    }
  }
  
  if (!validateRequired(cardData.position)) {
    errors.position = 'Position is required';
  }
  
  if (cardData.contact_info?.email) {
    const emailValidation = validateEmail(cardData.contact_info.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }
  }
  
  if (cardData.contact_info?.phone) {
    const phoneValidation = validatePhone(cardData.contact_info.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.error;
    }
  }
  
  if (cardData.contact_info?.whatsapp) {
    const waValidation = validateWhatsApp(cardData.contact_info.whatsapp);
    if (!waValidation.isValid) {
      errors.whatsapp = waValidation.error;
    }
  }
  
  return errors;
};

// Format validation errors for display
export const formatValidationErrors = (errors) => {
  return Object.values(errors).join('. ');
};
